import { useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TextField,
  Button,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete,
  Tooltip,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DevicesIcon from '@mui/icons-material/Devices';
import FilterListIcon from '@mui/icons-material/FilterList';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmsIcon from '@mui/icons-material/Sms';
import RouterIcon from '@mui/icons-material/Router';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useEffectAsync } from '../../reactHelper';

const DeviceConfigTab = ({ t }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [deviceConfigs, setDeviceConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState(null);
  const [protocolFilter, setProtocolFilter] = useState(null);
  const [brands, setBrands] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [editDialog, setEditDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [commandsDialog, setCommandsDialog] = useState(false);
  const [selectedCommands, setSelectedCommands] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchDeviceConfigs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (brandFilter) params.append('brand', brandFilter);
      if (protocolFilter) params.append('protocol', protocolFilter);

      const response = await fetch(`/api/device-config?${params}`);
      if (response.ok) {
        const data = await response.json();
        setDeviceConfigs(data);
      }
    } catch (e) {
      console.error('Error fetching device configs:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const [brandsRes, protocolsRes] = await Promise.all([
        fetch('/api/device-config/brands'),
        fetch('/api/server/protocols'),
      ]);
      if (brandsRes.ok) {
        setBrands(await brandsRes.json());
      }
      if (protocolsRes.ok) {
        const protocolData = await protocolsRes.json();
        setProtocols(Object.keys(protocolData));
      }
    } catch (e) {
      console.error('Error fetching filters:', e);
    }
  };

  useEffectAsync(async () => {
    await fetchFilters();
    await fetchDeviceConfigs();
  }, []);

  useEffectAsync(async () => {
    await fetchDeviceConfigs();
  }, [brandFilter, protocolFilter]);

  const filteredConfigs = useMemo(() => {
    if (!search) return deviceConfigs;
    const searchLower = search.toLowerCase();
    return deviceConfigs.filter((config) =>
      config.brand?.toLowerCase().includes(searchLower) ||
      config.model?.toLowerCase().includes(searchLower) ||
      config.protocol?.toLowerCase().includes(searchLower)
    );
  }, [deviceConfigs, search]);

  const paginatedConfigs = useMemo(() =>
    filteredConfigs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
  [filteredConfigs, page, rowsPerPage]);

  const parseCommands = (commandsStr) => {
    try {
      return typeof commandsStr === 'string' ? JSON.parse(commandsStr) : commandsStr;
    } catch {
      return null;
    }
  };

  const handleEdit = (item) => {
    setEditItem(item ? {
      ...item,
      commands: typeof item.commands === 'string' ? item.commands : JSON.stringify(item.commands, null, 2),
    } : {
      brand: '',
      model: '',
      protocol: '',
      port: '',
      commands: '{}',
      smssupport: true,
      gprssupport: true,
      active: true,
    });
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      let commandsJson;
      try {
        commandsJson = JSON.parse(editItem.commands || '{}');
      } catch {
        setSnackbar({ open: true, message: t('jsonInvalidError'), severity: 'error' });
        return;
      }

      const saveData = {
        ...editItem,
        commands: JSON.stringify(commandsJson),
        port: editItem.port ? parseInt(editItem.port, 10) : null,
      };

      const isNew = !editItem.id;
      const url = isNew ? '/api/device-config' : `/api/device-config/${editItem.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(saveData),
      });

      if (response.ok) {
        setSnackbar({ open: true, message: t('sharedSaved'), severity: 'success' });
        setEditDialog(false);
        fetchDeviceConfigs();
        fetchFilters();
      } else {
        throw new Error('Failed to save');
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`/api/device-config/${deleteItem.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setSnackbar({ open: true, message: t('sharedRemoved'), severity: 'success' });
        setDeleteDialog(false);
        fetchDeviceConfigs();
        fetchFilters();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleCopyCommand = (command) => {
    navigator.clipboard.writeText(command);
    setSnackbar({ open: true, message: t('sharedCopied'), severity: 'info' });
  };

  const handleViewCommands = (config) => {
    const commands = parseCommands(config.commands);
    setSelectedCommands({ config, commands });
    setCommandsDialog(true);
  };

  return (
    <>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <DevicesIcon color="primary" />
            <Typography variant="h6">
              {t('configDevices')}
            </Typography>
            <Chip
              size="small"
              label={`${filteredConfigs.length} ${t('sharedTotal').toLowerCase()}`}
              color="primary"
              variant="outlined"
            />
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDeviceConfigs}
              disabled={loading}
              size={isMobile ? 'small' : 'medium'}
            >
              {t('sharedRefresh')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleEdit(null)}
              size={isMobile ? 'small' : 'medium'}
            >
              {t('sharedAdd')}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('sharedSearch')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Autocomplete
              size="small"
              options={brands}
              value={brandFilter}
              onChange={(_, value) => {
                setBrandFilter(value);
                setPage(0);
              }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Box component="li" key={option} {...rest}>
                    {option}
                  </Box>
                );
              }}
              slotProps={{
                paper: { sx: { minWidth: 300 } },
                listbox: { sx: { maxHeight: 350 } },
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('deviceBrandConfig')} />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Autocomplete
              size="small"
              options={protocols}
              value={protocolFilter}
              onChange={(_, value) => {
                setProtocolFilter(value);
                setPage(0);
              }}
              renderOption={(props, option) => {
                const { key, ...rest } = props;
                return (
                  <Box component="li" key={option} {...rest}>
                    {option}
                  </Box>
                );
              }}
              slotProps={{
                paper: { sx: { minWidth: 300 } },
                listbox: { sx: { maxHeight: 350 } },
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('sharedProtocol')} />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => {
                setBrandFilter(null);
                setProtocolFilter(null);
                setSearch('');
              }}
              size={isMobile ? 'small' : 'medium'}
            >
              {t('reportClear')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100' }}>
                  <TableCell>{t('deviceBrandConfig')}</TableCell>
                  <TableCell>{t('deviceModelConfig')}</TableCell>
                  <TableCell>{t('sharedProtocol')}</TableCell>
                  {!isMobile && <TableCell>{t('sharedPort')}</TableCell>}
                  {!isMobile && <TableCell align="center">{t('deviceSmsSupport')}</TableCell>}
                  {!isMobile && <TableCell align="center">{t('deviceGprsSupport')}</TableCell>}
                  <TableCell>{t('deviceCommands')}</TableCell>
                  <TableCell align="right">{t('sharedActions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedConfigs.map((config) => {
                  const commands = parseCommands(config.commands);
                  const commandCount = commands ? Object.keys(commands).length : 0;

                  return (
                    <TableRow
                      key={config.id}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {config.brand}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {config.model}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={config.protocol}
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{
                              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              display: 'inline-block',
                            }}
                          >
                            {config.port}
                          </Typography>
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell align="center">
                          {config.smssupport ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <CancelIcon color="disabled" fontSize="small" />
                          )}
                        </TableCell>
                      )}
                      {!isMobile && (
                        <TableCell align="center">
                          {config.gprssupport ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <CancelIcon color="disabled" fontSize="small" />
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {commandCount > 0 ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewCommands(config)}
                          >
                            {commandCount} {t('commandTemplates').toLowerCase()}
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(config)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeleteItem(config);
                            setDeleteDialog(true);
                          }}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredConfigs.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={t('sharedRowsPerPage')}
              rowsPerPageOptions={[10, 15, 25, 50]}
            />
          </>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          {editItem?.id ? t('sharedEdit') : t('sharedAdd')} - {t('configDevices')}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('deviceBrandConfig')}
                value={editItem?.brand || ''}
                onChange={(e) => setEditItem({ ...editItem, brand: e.target.value })}
                required
                placeholder="Ex: Teltonika"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('deviceModelConfig')}
                value={editItem?.model || ''}
                onChange={(e) => setEditItem({ ...editItem, model: e.target.value })}
                required
                placeholder="Ex: FMB920"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                options={protocols}
                value={editItem?.protocol || ''}
                onChange={(_, value) => setEditItem({ ...editItem, protocol: value })}
                onInputChange={(_, value) => setEditItem({ ...editItem, protocol: value })}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <Box component="li" key={option} {...rest}>
                      {option}
                    </Box>
                  );
                }}
                slotProps={{
                  paper: { sx: { minWidth: 350 } },
                  listbox: { sx: { maxHeight: 350 } },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('sharedProtocol')}
                    required
                    placeholder="Ex: teltonika"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('sharedPort')}
                type="number"
                value={editItem?.port || ''}
                onChange={(e) => setEditItem({ ...editItem, port: e.target.value })}
                placeholder="Ex: 5027"
              />
            </Grid>
            <Grid size={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <SmsIcon color={editItem?.smssupport ? 'primary' : 'disabled'} />
                <Typography>{t('deviceSmsSupport')}</Typography>
                <Button
                  size="small"
                  variant={editItem?.smssupport ? 'contained' : 'outlined'}
                  onClick={() => setEditItem({ ...editItem, smssupport: !editItem?.smssupport })}
                >
                  {editItem?.smssupport ? t('sharedYes') : t('sharedNo')}
                </Button>
              </Box>
            </Grid>
            <Grid size={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <RouterIcon color={editItem?.gprssupport ? 'primary' : 'disabled'} />
                <Typography>{t('deviceGprsSupport')}</Typography>
                <Button
                  size="small"
                  variant={editItem?.gprssupport ? 'contained' : 'outlined'}
                  onClick={() => setEditItem({ ...editItem, gprssupport: !editItem?.gprssupport })}
                >
                  {editItem?.gprssupport ? t('sharedYes') : t('sharedNo')}
                </Button>
              </Box>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('deviceCommands')} (JSON)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={editItem?.commands || '{}'}
                onChange={(e) => setEditItem({ ...editItem, commands: e.target.value })}
                InputProps={{
                  sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                }}
                placeholder={`{
  "restart": {
    "name": "Restart",
    "gprs": "cpureset",
    "sms": "reset123456"
  },
  "status": {
    "name": "Status",
    "gprs": "getinfo"
  }
}`}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          <Button onClick={() => setEditDialog(false)}>
            {t('sharedCancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!editItem?.brand || !editItem?.model || !editItem?.protocol}
          >
            {t('sharedSave')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commands Dialog */}
      <Dialog open={commandsDialog} onClose={() => setCommandsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          {selectedCommands?.config?.brand} {selectedCommands?.config?.model} - {t('deviceCommands')}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          {selectedCommands?.commands && Object.entries(selectedCommands.commands).map(([key, cmd]) => (
            <Accordion
              key={key}
              defaultExpanded={Object.keys(selectedCommands.commands).length <= 3}
              sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontWeight="medium">{cmd.name || key}</Typography>
                  {cmd.gprs && (
                    <Chip size="small" icon={<RouterIcon />} label="GPRS" variant="outlined" />
                  )}
                  {cmd.sms && (
                    <Chip size="small" icon={<SmsIcon />} label="SMS" variant="outlined" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {cmd.gprs && (
                    <Grid size={12}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <RouterIcon color="primary" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">GPRS:</Typography>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{
                            bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.100',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            flex: 1,
                          }}
                        >
                          {cmd.gprs}
                        </Typography>
                        <IconButton size="small" onClick={() => handleCopyCommand(cmd.gprs)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  )}
                  {cmd.sms && (
                    <Grid size={12}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <SmsIcon color="secondary" fontSize="small" />
                        <Typography variant="caption" color="text.secondary">SMS:</Typography>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          sx={{
                            bgcolor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.100',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            flex: 1,
                          }}
                        >
                          {cmd.sms}
                        </Typography>
                        <IconButton size="small" onClick={() => handleCopyCommand(cmd.sms)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          <Button onClick={() => setCommandsDialog(false)}>
            {t('sharedCancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          {t('sharedRemoveConfirm')}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          <Typography>
            {deleteItem?.brand} {deleteItem?.model}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper' }}>
          <Button onClick={() => setDeleteDialog(false)}>
            {t('sharedCancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('sharedRemove')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </>
  );
};

export default DeviceConfigTab;
